import { useEffect, useMemo } from 'react';

import first from 'lodash/first';

import { TeamNanoID } from '../../../teams/teamsTypes';
import { GeneralLedgerGeneralLedgerTypes } from '../../../generalLedgers/generalLedgersTypes';

import {
  FETCH_CACHE_ACCOUNTS_QUERY,
  FetchCacheAccountsQueryResponse
} from '../../queries/fetchCacheAccounts.query';
import {
  FETCH_CACHE_GENERAL_LEDGERS_QUERY,
  FetchCacheGeneralLedgersQueryResponse
} from '../../../generalLedgers/queries/fetchCacheGeneralLedgers.query';

import { useCachePaginatedAccounts } from '../useCachePaginatedAccounts';
import { useCachePaginatedGeneralLedgers } from '../../../generalLedgers/hooks/useCachePaginatedGeneralLedgers';
import { usePreviousValue } from '../../../../common/hooks/usePreviousValue';

import { AccountCache } from '../../AccountCache';
import { GeneralLedgerCache } from '../../../generalLedgers/GeneralLedgerCache';

import {
  CASH_ACCOUNT_TYPE_CHART_NUMBER,
  REVENUE_REVERSE_DEBT_ACCOUNT_TYPE_CHART_NUMBER
} from '../../accountsConstants';
import filter from 'lodash/filter';
import { AccountBalance } from '../../accountsTypes';

type GetBalanceOptions = {
  companyCashAccount: { balance: AccountBalance };
  companyRevenueReverseDebtAccount: { balance: AccountBalance };
};

const getBalance = ({
  companyCashAccount,
  companyRevenueReverseDebtAccount
}: GetBalanceOptions) =>
  (companyCashAccount?.balance || 0) -
  (companyRevenueReverseDebtAccount?.balance || 0);

interface CompanyBalanceAccountsProps {
  companyNanoId: TeamNanoID;
}

function useCompanyBalanceAccounts({
  companyNanoId
}: CompanyBalanceAccountsProps) {
  const prevCompanyNanoId = usePreviousValue(companyNanoId);

  const {
    generalLedgers,
    generalLedgersFetched,
    generalLedgersError,
    changeGeneralLedgersFilters
  } = useCachePaginatedGeneralLedgers<FetchCacheGeneralLedgersQueryResponse>({
    cacheKey:
      GeneralLedgerCache.companyDefaultGeneralLedgersCacheKey(companyNanoId),
    query: FETCH_CACHE_GENERAL_LEDGERS_QUERY,
    initialFilters: {
      generalLedgerType: { eq: GeneralLedgerGeneralLedgerTypes.DEFAULT },
      companyNanoId: { eq: companyNanoId }
    },
    options: {
      enabled: !!companyNanoId,
      enabledPlaceholder: !!companyNanoId,
      withoutPrefetch: true
    }
  });

  const prevGeneralLedgersId = usePreviousValue(first(generalLedgers)?.id);

  const {
    accounts,
    accountsErrorMessage,
    accountsFetched,
    changeAccountsFilters
  } = useCachePaginatedAccounts<FetchCacheAccountsQueryResponse>({
    query: FETCH_CACHE_ACCOUNTS_QUERY,
    cacheKey: AccountCache.companyBalanceAccountsCacheKey(companyNanoId),
    initialFilters: {
      accountTypeChartNumber: {
        in: [
          CASH_ACCOUNT_TYPE_CHART_NUMBER,
          REVENUE_REVERSE_DEBT_ACCOUNT_TYPE_CHART_NUMBER
        ]
      },
      companyNanoId: { eq: companyNanoId },
      generalLedgerId: generalLedgers
        ? { eq: first(generalLedgers)?.id }
        : undefined
    },
    options: {
      enabled: !!companyNanoId && generalLedgersFetched,
      enabledPlaceholder: !!companyNanoId && generalLedgersFetched,
      withoutPrefetch: true
    }
  });

  useEffect(() => {
    if (prevCompanyNanoId !== companyNanoId) {
      changeAccountsFilters({
        companyNanoId: { eq: companyNanoId }
      });
      changeGeneralLedgersFilters({
        companyNanoId: { eq: companyNanoId }
      });
    }
  }, [
    changeAccountsFilters,
    changeGeneralLedgersFilters,
    companyNanoId,
    prevCompanyNanoId
  ]);

  useEffect(() => {
    if (
      prevGeneralLedgersId !== first(generalLedgers)?.id &&
      first(generalLedgers)?.id
    ) {
      changeAccountsFilters({
        generalLedgerId: { eq: first(generalLedgers)?.id }
      });
    }
  }, [
    changeAccountsFilters,
    generalLedgers,
    prevCompanyNanoId,
    prevGeneralLedgersId
  ]);

  const companyCashAccount =
    first(
      filter(
        accounts,
        (account) =>
          account.accountType.chartNumber === CASH_ACCOUNT_TYPE_CHART_NUMBER
      )
    ) || null;

  const companyRevenueReverseDebtAccount =
    first(
      filter(
        accounts,
        (account) =>
          account.accountType.chartNumber ===
          REVENUE_REVERSE_DEBT_ACCOUNT_TYPE_CHART_NUMBER
      )
    ) || null;

  const companyBalance = useMemo(
    () =>
      getBalance({
        companyCashAccount,
        companyRevenueReverseDebtAccount
      }),
    [companyCashAccount, companyRevenueReverseDebtAccount]
  );

  return {
    companyBalance,
    companyCashAccount,
    companyRevenueReverseDebtAccount,
    companyBalanceAccountsErrorMessage:
      accountsErrorMessage || generalLedgersError,
    companyBalanceAccountsFetched: accountsFetched
  };
}

export default useCompanyBalanceAccounts;
